import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { DialogDescription, DialogHeader, DialogPopup, DialogTitle } from '../../../web/components-v2/Dialog';
import { IDemoRecording, useDemoRecordingsQuery } from '../../queries/DemoRecordings/useDemoRecordings';
import { RecordingItem } from './RecordingItem';
import { styleSheet } from './styles';

export type { IDemoRecording } from '../../queries/DemoRecordings/useDemoRecordings';

interface IDemoRecordingsModalProps {
	demoId: string;
	demoDate: string;
	onStartAnalysis?: (recording: IDemoRecording) => void;
	analyzingRecordingId?: string | null;
	isAnalyzeLoading?: boolean;
}

export const DemoRecordingsModal: React.FC<IDemoRecordingsModalProps> = ({
	demoId,
	demoDate,
	onStartAnalysis,
	analyzingRecordingId,
	isAnalyzeLoading = false,
}) => {
	const { data, isLoading, error } = useDemoRecordingsQuery({
		demoId,
		demoDate,
	});

	const recordings = data?.recordings || [];
	const bestMatchIndex = data?.bestMatchIndex ?? null;

	const handlePlay = (url: string) => {
		// Open in new tab using window.open without window features (acts like a new tab)
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const handleDownloadTranscript = (url: string) => {
		const link = document.createElement('a');
		link.href = url;
		link.download = '';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleAnalyze = (recording: IDemoRecording) => {
		if (onStartAnalysis) {
			onStartAnalysis(recording);
		}
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return '';
		return moment(dateString).format('dddd, MMMM Do, YYYY');
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className={css(styleSheet.loadingState)}>
					<div className={css(styleSheet.loadingSpinner)} />
					<div className={css(styleSheet.loadingText)}>Loading demo recordings...</div>
				</div>
			);
		}

		if (error) {
			return (
				<div className={css(styleSheet.errorState)}>
					<div className={css(styleSheet.errorMessage)}>
						{error instanceof Error ? error.message : 'Failed to load demo recordings. Please try again.'}
					</div>
				</div>
			);
		}

		if (recordings.length === 0) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<div>No recordings found for this demo date.</div>
				</div>
			);
		}

		return (
			<div className={css(styleSheet.recordingsList)}>
				{recordings.map((recording, index) => (
					<RecordingItem
						key={`${recording.id}-${recording.uuid}-${index}`}
						recording={recording}
						isBestMatch={index === bestMatchIndex}
						onPlay={handlePlay}
						onDownloadTranscript={handleDownloadTranscript}
						onAnalyze={onStartAnalysis ? handleAnalyze : undefined}
						isAnalyzeLoading={isAnalyzeLoading && analyzingRecordingId === recording.uuid}
					/>
				))}
			</div>
		);
	};

	return (
		<DialogPopup styles={[styleSheet.wideDialog]}>
			<DialogHeader>
				<DialogTitle>Demo Recordings</DialogTitle>
				<DialogDescription>{formatDate(demoDate)}</DialogDescription>
			</DialogHeader>
			<div className={css(styleSheet.modalContent)} role='main' aria-label='Demo recordings list'>
				{renderContent()}
			</div>
		</DialogPopup>
	);
};
